/**
 * Hindi catalog (Devanagari) for the Phase 2 `MessageId` set — Deliverable B.
 *
 * `dir: 'ltr'` (Devanagari is left-to-right). The script needs complex shaping —
 * conjuncts (े.g. क्ष, द्ध) and matra reordering — which is delegated entirely to the
 * platform shaper via `fontStack('devanagari')`; the UI does NO glyph measuring or slicing.
 * The catalog supplies plain Unicode text and lets the shaper compose it.
 *
 * W/M grades stay Latin (`::numbering-system/latn`) as stable technical tokens. Hindi is
 * LTR so no bidi isolation is required, but the renderer may still isolate ids uniformly.
 */
import { P2 } from '../message-ids-phase2.js';
export const HI_CATALOG = {
    locale: 'hi',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'जाँच',
        [P2.sectionClaims]: 'दावे',
        [P2.sectionIntegrity]: 'अखंडता',
        [P2.axisWarrantLabel]: 'औचित्य',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'अनुभवजन्य',
        [P2.axisEmpiricalValue]: '{kind, select, defn {परिभाषा} interp {व्याख्या} testable {परीक्षण-योग्य} tested {परीक्षित} refuted {खंडित} program {अनुसंधान कार्यक्रम} philos {दार्शनिक} other {अज्ञात}}',
        [P2.axisModalLabel]: 'रीतिपरक',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'मज़बूती स्तर {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'अन्वेषण-योग्य',
        [P2.stateRefuted]: 'खंडित',
        [P2.stateOutside]: 'दायरे के बाहर',
        [P2.verdictConflict]: '{constraints} से टकराव',
        [P2.verdictOutside]: '{missing} आवश्यक',
        [P2.lossRegisterPlain]: 'सरल भाषा में संक्षेपित — कुछ सटीकता छोड़ दी गई है।',
        [P2.lossRegisterKid]: 'छोटे पाठकों के लिए सरल — कई विवरण छोड़ दिए गए हैं।',
        [P2.lossProfileDetail]: 'पूर्ण औचित्य/अनुभवजन्य/रीतिपरक प्रोफ़ाइल यहाँ संक्षेपित है।',
        [P2.lossConflictDetail]: 'इस दृश्य में टकराव का विवरण संक्षिप्त है।',
        [P2.laneVerified]: 'सत्यापित',
        [P2.laneUnverified]: 'असत्यापित',
        [P2.laneConflict]: 'टकराव सिद्ध',
        [P2.constraintLogic]: 'तर्कशास्त्र',
        [P2.constraintLightBends]: 'गुरुत्वाकर्षण क्षेत्र में प्रकाश का मुड़ना',
        [P2.constraintParallelPostulate]: 'समांतर अभिगृहीत',
        [P2.claimScalarGravity]: 'गुरुत्वाकर्षण का अदिश सिद्धांत',
        [P2.claimNonEuclidean]: 'अयूक्लिडीय ज्यामिति',
        [P2.claimSubstrateSr]: 'विशेष सापेक्षता की आधार-व्याख्या',
        [P2.edgeExtObservation]: 'बाहरी प्रेक्षण',
        [P2.edgeResourceSample]: 'साझा संगणकीय संसाधन',
        [P2.edgeHeuristicSample]: 'अनुमानमूलक मार्गदर्शन',
        [P2.edgeSharedSample]: 'साझा आधार',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'बाध्यताएँ',
        [P2.sectionEdges]: 'किनारे',
        [P2.sectionFrame]: 'ढाँचा',
        [P2.sectionExposure]: 'अनावरण',
        [P2.statusAdoptedFormal]: 'अपनाया गया (औपचारिक)',
        [P2.statusAdoptedInterpretive]: 'अपनाया गया (व्याख्यात्मक)',
        [P2.statusSuspended]: 'निलंबित',
        [P2.edgeEffectConstraint]: 'एक बाध्यता जोड़ता है (खंडन कर सकता है)',
        [P2.edgeEffectResource]: 'संसाधन — निर्णयों पर कोई प्रभाव नहीं',
        [P2.edgeEffectHeuristic]: 'स्वानुमान — निर्णयों पर कोई प्रभाव नहीं',
        [P2.edgeEffectShared]: 'साझा आधार — निर्णयों पर कोई प्रभाव नहीं',
        [P2.frameActive]: '{count, plural, one {# सक्रिय बाध्यता} other {# सक्रिय बाध्यताएँ}}',
        [P2.exposureNone]: 'कोई व्याख्यात्मक निर्भरता नहीं',
        [P2.exposureDep]: '{id} पर निर्भर',
        [P2.laneTrackMachine]: 'मशीन',
        [P2.laneTrackExpert]: 'विशेषज्ञ',
        [P2.laneTrackCommunity]: 'समुदाय',
        [P2.expertAudited]: 'लेखापरीक्षित',
        [P2.expertInProgress]: 'प्रगति में',
        [P2.expertNotReviewed]: 'समीक्षा नहीं हुई',
        [P2.communityFlags]: 'चिह्न मौजूद',
        [P2.communityNone]: 'कोई चिह्न नहीं',
        [P2.controlChallenge]: 'चुनौती',
        [P2.controlActivate]: 'सक्रिय करें',
        [P2.controlMakeInterpretive]: 'व्याख्यात्मक बनाएँ',
        [P2.controlImportEdge]: 'किनारा आयात करें',
        [P2.controlPromote]: 'पदोन्नत करें',
        [P2.controlUndo]: 'पूर्ववत करें',
        [P2.controlRedo]: 'फिर से करें',
        [P2.controlRegister]: 'पंजीकृत करें',
        [P2.promoteRejected]: 'पदोन्नति अस्वीकृत',
        [P2.promoteAccepted]: 'पदोन्नति स्वीकृत',
        [P2.promoteReasonRefutationTerminal]: 'प्रमाण खंडन को ठीक नहीं कर सकता।',
        [P2.promoteReasonKeystone]: 'मुख्य पदोन्नति के लिए अनुभवजन्य समर्थन आवश्यक है।',
        [P2.promoteReasonEmpiricalNotFixed]: 'अनुभवजन्य प्रकार निश्चित नहीं है।',
        [P2.promoteReasonModalNotFixed]: 'रीतिपरक श्रेणी निश्चित नहीं है।',
        [P2.promoteReasonWarrantNotImproved]: 'औचित्य श्रेणी में सुधार नहीं हुआ।',
        [P2.evidenceComputation]: "{engine} ने {config} के अंतर्गत {op} = {result} परिकलित किया",
        [P2.lossNumericRounding]: "परिणाम {dp} दशमलव स्थानों तक पूर्णांकित",
    },
};
//# sourceMappingURL=hi.js.map